import { useMemo, useState } from "react";
import { toast } from "../components/Toast";
import { DateInput, DateTimeInput } from "../components/DateInput";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useOrg } from "../lib/session";
import { ago } from "../lib/format";
import { money, toMajor, toMinor } from "../lib/plans";
import type { Member } from "../lib/types";
import { Empty, ErrorText, Field, Form, Loading, PageHeader, str } from "../components/ui";
import { CustomFields } from "../components/CustomFields";
import { useWords } from "../lib/workspace";
import { registerWarmer } from "../lib/smooth";

export interface Stage {
  id: string;
  name: string;
  position: number;
  kind: "open" | "won" | "lost";
  win_probability: number;
}

export interface Pipeline {
  id: string;
  name: string;
  is_default: boolean;
  stages: Stage[];
}

export interface Deal {
  id: string;
  pipeline_id: string;
  stage_id: string;
  stage_name: string;
  contact_id: string | null;
  contact_name: string | null;
  owner_member_id: string | null;
  owner_name: string | null;
  title: string;
  amount_minor: number | null;
  currency: string;
  expected_close_on: string | null;
  status: "open" | "won" | "lost";
  lost_reason: string | null;
  source: string;
  stage_entered_at: string;
  custom?: Record<string, unknown>;
  created_at: string;
}

interface Summary {
  currency: string;
  open_deals: number;
  open_amount_minor: number;
  weighted_amount_minor: number;
  won_this_month: number;
  won_this_month_amount_minor: number;
  win_rate_90_days: number | null;
  avg_days_to_win: number | null;
  mixed_currencies: boolean;
}

const pipelinesQuery = (orgId: string, base: string) => ({ queryKey: [orgId, "pipelines"], queryFn: () => api<{ pipelines: Pipeline[] }>(`${base}/pipelines`) });
registerWarmer("deals", (qc, { orgId, base }) => { qc.prefetchQuery({ ...pipelinesQuery(orgId, base), staleTime: 10_000 }); });

export function usePipelines() {
  const { base, orgId } = useOrg();
  return useQuery(pipelinesQuery(orgId, base));
}

export function useForecast(pipeline?: string) {
  const { base, orgId } = useOrg();
  return useQuery({ queryKey: [orgId, "deals", "summary", pipeline ?? "default"], queryFn: () => api<Summary>(`${base}/deals/summary${pipeline ? `?pipeline=${pipeline}` : ""}`) });
}

const pct = (n: number | null) => (n == null ? "—" : `${Math.round(n * 100)}%`);

export function ForecastStats({ pipeline, to }: { pipeline?: string; to?: string }) {
  const s = useForecast(pipeline);
  if (!s.data) return null;
  const d = s.data;
  const Tile = ({ label, value }: { label: string; value: string }) =>
    to ? <Link className="stat" to={to}><b>{value}</b><span>{label}</span></Link> : <div className="stat"><b>{value}</b><span>{label}</span></div>;
  return (
    <section className="stats">
      <Tile label={`Open pipeline (${d.open_deals} deals)`} value={money(d.open_amount_minor, d.currency)} />
      <Tile label="Weighted forecast" value={money(d.weighted_amount_minor, d.currency)} />
      <Tile label={`Won this month (${d.won_this_month})`} value={money(d.won_this_month_amount_minor, d.currency)} />
      <Tile label="Win rate (90 days)" value={pct(d.win_rate_90_days)} />
    </section>
  );
}

export function Deals() {
  const { base, orgId, allows } = useOrg();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const pipelines = usePipelines();
  const pipelineId = params.get("pipeline") ?? pipelines.data?.pipelines.find((p) => p.is_default)?.id;
  const pipeline = pipelines.data?.pipelines.find((p) => p.id === pipelineId);
  const [mine, setMine] = useState(false);
  const [adding, setAdding] = useState(false);
  const [dragging, setDragging] = useState<string | null>(null);
  const [over, setOver] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const w = useWords();
  const key = [orgId, "deals", pipelineId, mine];
  const deals = useQuery({
    queryKey: key,
    queryFn: () => api<{ deals: Deal[] }>(`${base}/deals?pipeline=${pipelineId}${mine ? "&mine=true" : ""}`),
    enabled: !!pipelineId,
  });
  const byStage = useMemo(() => {
    const m = new Map<string, Deal[]>();
    deals.data?.deals.forEach((d) => m.set(d.stage_id, [...(m.get(d.stage_id) ?? []), d]));
    return m;
  }, [deals.data]);

  async function move(dealId: string, stageId: string) {
    setError(null);
    const prev = qc.getQueryData<{ deals: Deal[] }>(key);
    const stage = pipeline?.stages.find((s) => s.id === stageId);
    // Optimistic: closed deals leave the board, open ones change column.
    qc.setQueryData(key, (old: { deals: Deal[] } | undefined) => old && {
      deals: stage?.kind === "open" ? old.deals.map((d) => (d.id === dealId ? { ...d, stage_id: stageId, stage_name: stage.name } : d)) : old.deals.filter((d) => d.id !== dealId),
    });
    try {
      await api(`${base}/deals/${dealId}`, { method: "PATCH", json: { stage_id: stageId } });
    } catch (err) {
      setError(err);
      qc.setQueryData(key, prev);
    } finally {
      qc.invalidateQueries({ queryKey: [orgId, "deals", "summary"] });
    }
  }

  if (pipelines.isLoading) return <Loading />;
  if (pipelines.error) return <ErrorText error={pipelines.error} />;
  if (!pipeline) return <Empty>No pipeline.</Empty>;
  const open = pipeline.stages.filter((s) => s.kind === "open");
  const closed = pipeline.stages.filter((s) => s.kind !== "open");
  return (
    <>
      <PageHeader
        title={w("deal", true)}
        actions={
          <>
            {pipelines.data!.pipelines.length > 1 && (
              <select aria-label="Pipeline" value={pipeline.id} onChange={(e) => setParams({ pipeline: e.target.value })}>
                {pipelines.data!.pipelines.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            )}
            <label className="inline small"><input type="checkbox" checked={mine} onChange={(e) => setMine(e.target.checked)} /> Only mine</label>
            {allows("pipelines") && <Link to={`/o/${orgId}/deals/pipelines`}>Edit pipelines</Link>}
            <button onClick={() => setAdding(!adding)}>New {w.lower("deal")}</button>
          </>
        }
      />
      <ForecastStats pipeline={pipeline.id} />
      {adding && <NewDeal pipeline={pipeline} onDone={(id) => { setAdding(false); qc.invalidateQueries({ queryKey: [orgId, "deals"] }); if (id) navigate(`/o/${orgId}/deals/${id}`); }} />}
      <ErrorText error={error} />
      {deals.isLoading ? <Loading /> : (
        <div className="board">
          {open.map((s) => {
            const list = byStage.get(s.id) ?? [];
            const total = list.reduce((n, d) => n + (d.amount_minor ?? 0), 0);
            return (
              <div key={s.id} className={`column${dragging ? " droppable" : ""}${over === s.id ? " over" : ""}`}
                onDragOver={(e) => { e.preventDefault(); if (over !== s.id) setOver(s.id); }}
                onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setOver((o) => (o === s.id ? null : o)); }}
                onDrop={(e) => { e.preventDefault(); const id = e.dataTransfer.getData("text/deal"); if (id) move(id, s.id); setDragging(null); setOver(null); }}>
                <div className="column-head">
                  <b>{s.name}</b>
                  <span className="muted small">{list.length} · {money(total, list[0]?.currency ?? "BHD")} · {s.win_probability}%</span>
                </div>
                {list.map((d) => (
                  <Link key={d.id} to={`/o/${orgId}/deals/${d.id}`} className={`deal-card${dragging === d.id ? " lifted" : ""}`} draggable
                    onDragStart={(e) => { e.dataTransfer.setData("text/deal", d.id); e.dataTransfer.effectAllowed = "move"; setDragging(d.id); }} onDragEnd={() => { setDragging(null); setOver(null); }}>
                    <b>{d.title}</b>
                    {d.contact_name && <span className="small">{d.contact_name}</span>}
                    <span className="small">{d.amount_minor != null ? money(d.amount_minor, d.currency) : <span className="muted">No value</span>}</span>
                    <span className="muted small">{d.owner_name ?? "Unassigned"} · in stage {ago(d.stage_entered_at)}{d.source === "agent" ? " · from AI" : ""}</span>
                  </Link>
                ))}
              </div>
            );
          })}
        </div>
      )}
      {dragging && (
        <div className="close-zones">
          {closed.map((s) => (
            <div key={s.id} className={`close-zone ${s.kind}${over === s.id ? " over" : ""}`} onDragOver={(e) => { e.preventDefault(); if (over !== s.id) setOver(s.id); }}
              onDragLeave={() => setOver((o) => (o === s.id ? null : o))}
              onDrop={(e) => { e.preventDefault(); const id = e.dataTransfer.getData("text/deal"); if (id) move(id, s.id); setDragging(null); setOver(null); }}>
              Drop to mark {s.name}
            </div>
          ))}
        </div>
      )}
      <ClosedDeals pipeline={pipeline} />
    </>
  );
}

function ClosedDeals({ pipeline }: { pipeline: Pipeline }) {
  const { base, orgId } = useOrg();
  const [status, setStatus] = useState<"won" | "lost" | null>(null);
  const list = useQuery({
    queryKey: [orgId, "deals", pipeline.id, "closed", status],
    queryFn: () => api<{ deals: Deal[] }>(`${base}/deals?pipeline=${pipeline.id}&status=${status}`),
    enabled: !!status,
  });
  return (
    <section className="card">
      <div className="tabs small">
        <span className="muted small">Closed in the last 90 days:</span>
        <button className={status === "won" ? "" : "secondary"} onClick={() => setStatus(status === "won" ? null : "won")}>Won</button>
        <button className={status === "lost" ? "" : "secondary"} onClick={() => setStatus(status === "lost" ? null : "lost")}>Lost</button>
      </div>
      {status && (list.isLoading ? <Loading /> : list.data!.deals.length === 0 ? <Empty>None.</Empty> : (
        <table className="table compact">
          <tbody>
            {list.data!.deals.map((d) => (
              <tr key={d.id}>
                <td><Link to={`/o/${orgId}/deals/${d.id}`}>{d.title}</Link>{d.lost_reason && <div className="muted small">{d.lost_reason}</div>}</td>
                <td>{d.contact_name ?? ""}</td>
                <td className="num">{money(d.amount_minor, d.currency)}</td>
                <td className="muted small">{d.owner_name ?? "Unassigned"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ))}
    </section>
  );
}

export function NewDeal({ pipeline, contactId, onDone }: { pipeline?: Pipeline; contactId?: string; onDone: (id?: string) => void }) {
  const { base } = useOrg();
  const [currency, setCurrency] = useState("BHD");
  const w = useWords();
  return (
    <section className="card">
      <h2>New {w.lower("deal")}</h2>
      <Form submitLabel={`Create ${w.lower("deal")}`} onSubmit={async (d) => {
        const amount = str(d, "amount");
        const deal = await api<Deal>(`${base}/deals`, {
          method: "POST",
          json: {
            title: str(d, "title"), currency, ...(amount && { amount_minor: toMinor(amount, currency) }),
            ...(str(d, "close") && { expected_close_on: str(d, "close") }),
            ...(pipeline && { pipeline_id: pipeline.id }), ...(str(d, "stage") && { stage_id: str(d, "stage") }),
            ...(contactId && { contact_id: contactId }),
          },
        });
        onDone(deal.id);
      }}>
        <div className="row">
          <Field label="Title"><input name="title" required maxLength={200} placeholder="e.g. Office fit-out for Acme" /></Field>
          <Field label="Value"><input name="amount" type="number" min={0} step="any" /></Field>
          <Field label="Currency"><input value={currency} onChange={(e) => setCurrency(e.target.value.toUpperCase())} maxLength={3} pattern="[A-Za-z]{3}" /></Field>
        </div>
        <div className="row">
          {pipeline && (
            <Field label="Stage">
              <select name="stage" defaultValue="">
                {pipeline.stages.filter((s) => s.kind === "open").map((s, i) => <option key={s.id} value={i === 0 ? "" : s.id}>{s.name}</option>)}
              </select>
            </Field>
          )}
          <Field label="Expected close"><DateInput name="close" /></Field>
        </div>
      </Form>
      <button className="link small" onClick={() => onDone()}>Cancel</button>
    </section>
  );
}

interface DealItem { id: string; name: string; quantity: number; unit_price_minor: number; total_minor: number }
interface DealDetail extends Deal {
  history: { from_stage: string | null; to_stage: string | null; actor_type: string; actor_name: string | null; changed_at: string }[];
  items?: DealItem[];
}

export function DealPage() {
  const { dealId } = useParams();
  const { base, orgId, allows } = useOrg();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const key = [orgId, "deal", dealId];
  const deal = useQuery({ queryKey: key, queryFn: () => api<DealDetail>(`${base}/deals/${dealId}`) });
  const pipelines = usePipelines();
  const members = useQuery({ queryKey: [orgId, "members"], queryFn: () => api<{ members: Member[] }>(`${base}/members`), retry: false });
  const tasks = useQuery({ queryKey: [orgId, "tasks", "deal", dealId], queryFn: () => api<{ tasks: { id: string; title: string; status: string; due_at: string | null }[] }>(`${base}/tasks?deal_id=${dealId}&status=all`) });
  const [error, setError] = useState<unknown>(null);
  const [losing, setLosing] = useState<string | null>(null);
  const [lostReason, setLostReason] = useState("");
  const w = useWords();
  if (deal.isLoading) return <Loading />;
  if (deal.error) return <ErrorText error={deal.error} />;
  const d = deal.data!;
  const ro = allows("readonly"); // analysts see, but don't change
  const pipeline = pipelines.data?.pipelines.find((p) => p.id === d.pipeline_id);
  const refresh = () => { qc.invalidateQueries({ queryKey: key }); qc.invalidateQueries({ queryKey: [orgId, "deals"] }); };
  async function patch(json: Record<string, unknown>) {
    setError(null);
    try {
      await api(`${base}/deals/${dealId}`, { method: "PATCH", json });
      refresh();
      toast("Saved");
    } catch (err) {
      setError(err);
    }
  }
  return (
    <>
      <PageHeader title={d.title} actions={<Link className="button-link secondary" to={`/o/${orgId}/deals?pipeline=${d.pipeline_id}`}>Back to board</Link>} />
      <p className="muted deal-sub">
        <span className={`stage-chip${d.status === "won" ? " stage-customer" : d.status === "lost" ? " stage-inactive" : " stage-lead"}`}>{d.status === "open" ? d.stage_name : d.status === "won" ? "Won" : "Lost"}</span>
        {d.status === "open" && <span>in this stage {ago(d.stage_entered_at).replace(/ ago$/, "")}</span>}
        {d.status === "lost" && d.lost_reason && <span>Reason: {d.lost_reason}</span>}
        <span className="num">{money(d.amount_minor, d.currency)}</span>
        {d.contact_id && <Link to={`/o/${orgId}/contacts/${d.contact_id}`}>{d.contact_name ?? "Contact"}</Link>}
        {d.source === "agent" && <span>Opened by the AI agent</span>}
      </p>
      <ErrorText error={error} />
      {pipeline && (
        <section className="card">
          <div className="stage-steps" role="group" aria-label="Stage">
            {pipeline.stages.filter((s) => s.kind === "open").map((s) => (
              <button key={s.id} disabled={ro} className={s.id === d.stage_id ? "" : "secondary"} aria-pressed={s.id === d.stage_id} onClick={() => s.id !== d.stage_id && patch({ stage_id: s.id })}>{s.name}</button>
            ))}
            <span className="spacer" />
            {pipeline.stages.filter((s) => s.kind === "won").map((s) => (
              <button key={s.id} disabled={ro} className={d.status === "won" ? "won-btn" : "secondary won-outline"} onClick={() => d.status !== "won" && patch({ stage_id: s.id })}>{d.status === "won" ? s.name : `Mark ${s.name.toLowerCase()}`}</button>
            ))}
            {pipeline.stages.filter((s) => s.kind === "lost").map((s) => (
              <button key={s.id} disabled={ro} className={d.status === "lost" ? "danger" : "secondary lost-outline"} onClick={() => d.status !== "lost" && setLosing(s.id)}>{d.status === "lost" ? s.name : `Mark ${s.name.toLowerCase()}`}</button>
            ))}
          </div>
          {losing && (
            <form className="lost-box" onSubmit={async (e) => { e.preventDefault(); await patch({ stage_id: losing, ...(lostReason.trim() && { lost_reason: lostReason.trim() }) }); setLosing(null); setLostReason(""); }}>
              <Field label="Why was it lost? (optional, helps you spot patterns)"><input autoFocus value={lostReason} maxLength={300} placeholder="e.g. Price too high, went with another clinic" onChange={(e) => setLostReason(e.target.value)} /></Field>
              <div className="inline">
                <button type="submit" className="danger">Mark as lost</button>
                <button type="button" className="link" onClick={() => { setLosing(null); setLostReason(""); }}>Cancel</button>
              </div>
            </form>
          )}
        </section>
      )}
      <DealItems deal={d} onSaved={refresh} readOnly={ro} />
      <div className="columns">
        <section className="card">
          <h2>Details</h2>
          <fieldset className="plain-fieldset" disabled={ro}>
          <Form submitLabel="Save" onSubmit={async (f) => {
            const amount = str(f, "amount");
            const owner = str(f, "owner");
            await patch({
              title: str(f, "title"), currency: str(f, "currency"),
              ...(amount ? { amount_minor: toMinor(amount, str(f, "currency") || d.currency) } : { clear_amount: true }),
              expected_close_on: str(f, "close"),
              ...(owner ? { owner_member_id: owner } : allows("contacts_all") ? { clear_owner: true } : {}),
            });
          }}>
            <Field label="Title"><input name="title" defaultValue={d.title} required maxLength={200} /></Field>
            <div className="row">
              <Field label={(d.items?.length ?? 0) > 0 ? "Value (from the lines above)" : "Value"}><input name="amount" type="number" min={0} step="any" defaultValue={toMajor(d.amount_minor, d.currency)} readOnly={(d.items?.length ?? 0) > 0} /></Field>
              <Field label="Currency"><input name="currency" defaultValue={d.currency} maxLength={3} /></Field>
              <Field label="Expected close"><DateInput name="close" defaultValue={d.expected_close_on ?? ""} /></Field>
            </div>
            <Field label="Owner">
              <select name="owner" defaultValue={d.owner_member_id ?? ""}>
                <option value="">Unassigned</option>
                {members.data?.members.filter((m) => m.status === "active" && m.crm_role).map((m) => <option key={m.id} value={m.id}>{m.display_name}</option>)}
              </select>
            </Field>
          </Form>
          </fieldset>
          {allows("delete") && (
            <button className="link small danger-link" onClick={async () => {
              if (!confirm(`Delete this ${w.lower("deal")}? Its history is removed too.`)) return;
              await api(`${base}/deals/${dealId}`, { method: "DELETE" });
              qc.invalidateQueries({ queryKey: [orgId, "deals"] });
              navigate(`/o/${orgId}/deals`);
            }}>Delete {w.lower("deal")}</button>
          )}
        </section>
        <section className="card">
          <h2>Tasks</h2>
          <ul className="list">
            {tasks.data?.tasks.map((t) => <li key={t.id} className={t.status === "done" ? "done" : ""}>{t.title}{t.due_at && <span className="muted small"> · due {ago(t.due_at)}</span>}</li>)}
          </ul>
          {!ro && <Form submitLabel="Add task" onSubmit={async (f) => {
            await api(`${base}/tasks`, { method: "POST", json: { title: str(f, "title"), deal_id: dealId, ...(str(f, "due") && { due_at: new Date(str(f, "due")).toISOString() }) } });
            qc.invalidateQueries({ queryKey: [orgId, "tasks"] });
          }}>
            <div className="row">
              <Field label="Next step"><input name="title" required maxLength={300} placeholder="e.g. Send the quote" /></Field>
              <Field label="Due"><DateTimeInput name="due" /></Field>
            </div>
          </Form>}
          <h2 style={{ marginTop: "1rem" }}>History</h2>
          <ul className="timeline">
            {d.history.slice().reverse().map((h, i) => (
              <li key={i}>
                <span>{h.from_stage ? `${h.from_stage} → ${h.to_stage}` : `Created in ${h.to_stage}`}</span>
                <small className="muted">{h.actor_name ?? ({ agent: "AI agent", automation: "Automation", system: "SVENEX" } as Record<string, string>)[h.actor_type] ?? h.actor_type} · {ago(h.changed_at)}</small>
              </li>
            ))}
          </ul>
        </section>
      </div>
      <CustomFields entity="deal" values={d.custom} canEdit={!ro} onSave={async (custom) => {
        await api(`${base}/deals/${dealId}`, { method: "PATCH", json: { custom } });
        refresh();
      }} />
    </>
  );
}

/** DealItems: what the deal is for, line by line; their total is the deal's value. */
function DealItems({ deal: d, onSaved, readOnly }: { deal: DealDetail; onSaved: () => void; readOnly?: boolean }) {
  const { base } = useOrg();
  const [rows, setRows] = useState<{ name: string; qty: string; price: string }[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  if (d.items === undefined) return null;
  const items = d.items;
  const editing = rows !== null;
  const start = () => setRows(items.length ? items.map((i) => ({ name: i.name, qty: String(i.quantity), price: toMajor(i.unit_price_minor, d.currency) })) : [{ name: "", qty: "1", price: "" }]);
  const lineTotal = (r: { qty: string; price: string }) => Math.round(Number(r.qty || 0) * (toMinor(r.price || "0", d.currency) ?? 0));
  const save = async () => {
    if (!rows) return;
    setBusy(true); setError(null);
    try {
      const clean = rows.filter((r) => r.name.trim());
      await api(`${base}/deals/${d.id}/items`, { method: "PUT", json: { items: clean.map((r) => ({ name: r.name.trim(), quantity: Number(r.qty), unit_price_minor: toMinor(r.price || "0", d.currency) ?? 0 })) } });
      setRows(null); onSaved(); toast("Lines saved");
    } catch (e) { setError(e); } finally { setBusy(false); }
  };
  if (!editing && items.length === 0) {
    if (readOnly) return null;
    return (
      <section className="card inline-card">
        <div><b>What's in this deal?</b><p className="muted small">Add lines like "2 x Whitening" and the value adds itself up.</p></div>
        <button className="secondary" onClick={start}>Add lines</button>
      </section>
    );
  }
  return (
    <section className="card">
      <div className="card-head"><h2>Lines</h2>{!editing && !readOnly && <button className="secondary small" onClick={start}>Edit</button>}</div>
      {!editing ? (
        <table className="table compact">
          <thead><tr><th>Item</th><th className="num">Qty</th><th className="num">Price</th><th className="num">Total</th></tr></thead>
          <tbody>
            {items.map((i) => <tr key={i.id}><td dir="auto">{i.name}</td><td className="num">{i.quantity}</td><td className="num">{money(i.unit_price_minor, d.currency)}</td><td className="num">{money(i.total_minor, d.currency)}</td></tr>)}
            <tr className="total-row"><td colSpan={3}>Total</td><td className="num"><b>{money(items.reduce((n, i) => n + i.total_minor, 0), d.currency)}</b></td></tr>
          </tbody>
        </table>
      ) : (
        <>
          <div className="item-rows">
            {rows!.map((r, i) => (
              <div key={i} className="item-row">
                <input aria-label="Item" placeholder="Item, e.g. Whitening" maxLength={120} value={r.name} onChange={(e) => setRows(rows!.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} />
                <input aria-label="Quantity" type="number" min={0.01} step="0.01" value={r.qty} onChange={(e) => setRows(rows!.map((x, j) => (j === i ? { ...x, qty: e.target.value } : x)))} />
                <input aria-label={`Price in ${d.currency}`} type="number" min={0} step="any" placeholder={`Price (${d.currency})`} value={r.price} onChange={(e) => setRows(rows!.map((x, j) => (j === i ? { ...x, price: e.target.value } : x)))} />
                <span className="num small">{money(lineTotal(r), d.currency)}</span>
                <button type="button" className="secondary small" aria-label="Remove line" onClick={() => setRows(rows!.filter((_, j) => j !== i))}>×</button>
              </div>
            ))}
          </div>
          <div className="inline">
            {rows!.length < 50 && <button type="button" className="secondary small" onClick={() => setRows([...rows!, { name: "", qty: "1", price: "" }])}>+ Line</button>}
            <span className="spacer" />
            <span>Total <b>{money(rows!.filter((r) => r.name.trim()).reduce((n, r) => n + lineTotal(r), 0), d.currency)}</b></span>
          </div>
          <ErrorText error={error} />
          <div className="inline" style={{ marginTop: ".6rem" }}>
            <button disabled={busy} onClick={save}>{busy ? "Saving…" : "Save lines"}</button>
            <button className="link" onClick={() => setRows(null)}>Cancel</button>
          </div>
        </>
      )}
    </section>
  );
}

export function Pipelines() {
  const { base, orgId } = useOrg();
  const qc = useQueryClient();
  const pipelines = usePipelines();
  const refresh = () => qc.invalidateQueries({ queryKey: [orgId, "pipelines"] });
  const w = useWords();
  if (pipelines.isLoading) return <Loading />;
  if (pipelines.error) return <ErrorText error={pipelines.error} />;
  return (
    <>
      <PageHeader title="Pipelines" actions={<Link to={`/o/${orgId}/deals`}>Back to {w.lower("deal", true)}</Link>} />
      {pipelines.data!.pipelines.map((p) => <PipelineEditor key={p.id} pipeline={p} onSaved={refresh} />)}
      <section className="card">
        <h2>New pipeline</h2>
        <Form submitLabel="Create" onSubmit={async (d) => { await api(`${base}/pipelines`, { method: "POST", json: { name: str(d, "name") } }); refresh(); }}>
          <Field label="Name (starts with the standard stages; edit them after)"><input name="name" required maxLength={80} placeholder="e.g. Renewals" /></Field>
        </Form>
      </section>
    </>
  );
}

type EditStage = { id?: string; name: string; kind: Stage["kind"]; win_probability: number };

function PipelineEditor({ pipeline, onSaved }: { pipeline: Pipeline; onSaved: () => void }) {
  const { base } = useOrg();
  const [name, setName] = useState(pipeline.name);
  const [stages, setStages] = useState<EditStage[]>(pipeline.stages.map(({ id, name, kind, win_probability }) => ({ id, name, kind, win_probability })));
  const [error, setError] = useState<unknown>(null);
  const [saved, setSaved] = useState(false);
  const set = (i: number, patch: Partial<EditStage>) => setStages(stages.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  const moveStage = (i: number, by: number) => {
    const j = i + by;
    if (j < 0 || j >= stages.length) return;
    const next = stages.slice();
    [next[i], next[j]] = [next[j], next[i]];
    setStages(next);
  };
  async function save() {
    setError(null);
    try {
      if (name !== pipeline.name) await api(`${base}/pipelines/${pipeline.id}`, { method: "PATCH", json: { name } });
      await api(`${base}/pipelines/${pipeline.id}/stages`, { method: "PUT", json: { stages } });
      onSaved();
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError(err);
    }
  }
  return (
    <section className="card">
      <div className="inline">
        <input value={name} onChange={(e) => setName(e.target.value)} maxLength={80} aria-label="Pipeline name" style={{ maxWidth: 320, fontWeight: 600 }} />
        {pipeline.is_default ? <span className="tag">default</span> : (
          <>
            <button className="link small" onClick={async () => { await api(`${base}/pipelines/${pipeline.id}`, { method: "PATCH", json: { is_default: true } }); onSaved(); }}>Make default</button>
            <button className="link small" onClick={async () => {
              setError(null);
              try { await api(`${base}/pipelines/${pipeline.id}`, { method: "DELETE" }); onSaved(); } catch (err) { setError(err); }
            }}>Remove</button>
          </>
        )}
      </div>
      <table className="table compact" style={{ marginTop: ".75rem" }}>
        <thead><tr><th>Stage</th><th>Type</th><th>Win chance</th><th></th></tr></thead>
        <tbody>
          {stages.map((s, i) => (
            <tr key={s.id ?? `new-${i}`}>
              <td><input value={s.name} maxLength={60} onChange={(e) => set(i, { name: e.target.value })} aria-label="Stage name" /></td>
              <td>
                <select value={s.kind} onChange={(e) => set(i, { kind: e.target.value as Stage["kind"] })} aria-label="Stage type">
                  <option value="open">Open</option><option value="won">Won</option><option value="lost">Lost</option>
                </select>
              </td>
              <td><input type="number" min={0} max={100} value={s.win_probability} onChange={(e) => set(i, { win_probability: Number(e.target.value) })} aria-label="Win chance" style={{ width: 80 }} />%</td>
              <td className="actions">
                <button className="secondary" aria-label="Move up" onClick={() => moveStage(i, -1)}>↑</button>
                <button className="secondary" aria-label="Move down" onClick={() => moveStage(i, 1)}>↓</button>
                <button className="secondary" aria-label="Remove stage" onClick={() => setStages(stages.filter((_, j) => j !== i))}>×</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="actions">
        <button className="secondary" onClick={() => setStages([...stages.slice(0, -2), { name: "", kind: "open", win_probability: 50 }, ...stages.slice(-2)])}>Add stage</button>
        <button onClick={save}>Save</button>
        {saved && <span className="ok-text small">Saved</span>}
      </div>
      <ErrorText error={error} />
    </section>
  );
}
